import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { AutoComplete } from 'primereact/autocomplete';
import { Dropdown } from 'primereact/dropdown';
import * as XLSX from 'xlsx';
import '../../css/Pages.css';
import UserListing from '../../components/UserManagement/User/UserListing';
import UserForm from '../../components/UserManagement/User/UserForm';
import ActivityLogModal from '../../components/UserManagement/User/ActivityLogModal';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';
import { useResetSCVDialog } from '../../hooks/useResetSCVDialog';
import AllGoogleScholarModal from '../../components/Profile/GoogleScholar/AllGoogleScholarModal';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, style }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label,searchKey"
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={style || { width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            emptyFilterMessage="Không tìm thấy kết quả"
        />
    );
};

const User = () => {
    const toast = useRef(null);
    const initialForm = {
        Username: '', Password: '', LastName: '', FirstName: '', Email: '', Phone: '',
        Gender: 'Nam', IsActive: true, IsDelegatedAdmin: false, DegreeId: '',
        AcademicTitleId: '', AcademicRank: '', DepartmentId: '', RoleId: '', ManagedDepartmentId: ''
    };

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [isScholarModalOpen, setIsScholarModalOpen] = useState(false);
    const [selectedScholarLink, setSelectedScholarLink] = useState('');

    const [formData, setFormData] = useState(initialForm);
    const [editId, setEditId] = useState(null);

    const [departments, setDepartments] = useState([]);
    const [roles, setRoles] = useState([]);
    const [degrees, setDegrees] = useState([]);
    const [academicTitles, setAcademicTitles] = useState([]);

    const [filters, setFilters] = useState({ departmentId: '', userId: null, roleId: '' });
    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [filteredAuthors, setFilteredAuthors] = useState([]);

    const [isMonitorModalOpen, setIsMonitorModalOpen] = useState(false);
    const [monitorLogs, setMonitorLogs] = useState([]);
    const [isMonitorLoading, setIsMonitorLoading] = useState(false);
    const [monitoredUser, setMonitoredUser] = useState(null);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();
    const { confirmResetSCVDialog } = useResetSCVDialog();

    const currentUser = JSON.parse(localStorage.getItem('user')) || {};
    const userRoleName = (currentUser.RoleName || '').toLowerCase();
    const isAdmin = currentUser.RoleId === 1 || userRoleName === 'quản trị viên';
    const isDelegatedManager = (currentUser.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser.IsDelegatedAdmin === true;
    const canManage = isAdmin || isDelegatedManager;

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const handleLogout = useCallback(() => {
        toast.current?.show({ severity: 'error', summary: 'Lỗi xác thực', detail: 'Phiên đăng nhập hết hạn hoặc không có quyền truy cập!', life: 3000 });
        setTimeout(() => {
            localStorage.removeItem('user');
            localStorage.removeItem('accessToken');
            window.location.href = '/login';
        }, 1500);
    }, []);

    const fetchDropdownData = useCallback(async () => {
        try {
            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const headers = getAuthHeaders();
            const [deptRes, roleRes, degreeRes, titleRes] = await Promise.all([
                fetch(`${baseUrl}/department`, { headers }),
                fetch(`${baseUrl}/role`, { headers }),
                fetch(`${baseUrl}/educationlevel`, { headers }),
                fetch(`${baseUrl}/jobtitle`, { headers })
            ]);

            if (deptRes.status === 401 || deptRes.status === 403) return handleLogout();

            if (deptRes.ok) setDepartments(await deptRes.json());
            if (roleRes.ok) setRoles(await roleRes.json());
            if (degreeRes.ok) setDegrees(await degreeRes.json());
            if (titleRes.ok) setAcademicTitles(await titleRes.json());
        } catch (error) {
            console.error("Lỗi tải dữ liệu dropdown:", error);
        }
    }, [getAuthHeaders, handleLogout]);

    const fetchData = useCallback(async (isSilent = false) => {
        if (!isSilent) setIsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (response.status === 401 || response.status === 403) return handleLogout();

            if (response.ok) {
                const result = await response.json();
                setData(result);
            }
        } catch (error) {
            console.error(error);
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi tải dữ liệu!' });
        } finally {
            if (!isSilent) setIsLoading(false);
        }
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        fetchData();
        fetchDropdownData();
    }, [fetchData, fetchDropdownData]);

    const filterUserOptions = useMemo(() => {
        return data.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || '';
            const dept = u.DepartmentName || '';
            const username = u.Username || '';
            const rawSearchStr = `${fullName} ${email} ${dept} ${username}`;

            return {
                label: fullName,
                searchKey: superCleanString(rawSearchStr),
                value: u.Id,
                fullName: fullName,
                email: email,
                dept: dept,
                username: username
            };
        });
    }, [data]);

    const searchAuthor = (event) => {
        const query = superCleanString(event.query);
        let _filteredAuthors;

        if (!query.trim().length) {
            _filteredAuthors = [...filterUserOptions];
        } else {
            _filteredAuthors = filterUserOptions.filter((user) => {
                return user.searchKey.includes(query);
            });
        }
        setFilteredAuthors(_filteredAuthors);
    };

    const userFilterTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName} ({option.username})</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {option.email} {option.dept ? ` - ${option.dept}` : ''}
                </span>
            </div>
        );
    };

    const departmentOptions = useMemo(() => {
        const options = [{ label: 'Tất cả đơn vị', value: 0, searchKey: 'tat ca don vi' }];
        const buildOptions = (parentId = null, level = 0) => {
            departments
                .filter(d => d.ParentId === parentId)
                .forEach(d => {
                    const prefix = level > 0 ? '\u00A0'.repeat(level * 4) + '˪ ' : '';
                    options.push({
                        label: prefix + d.Name,
                        value: d.Id,
                        searchKey: superCleanString(d.Name)
                    });
                    buildOptions(d.Id, level + 1);
                });
        };
        buildOptions(null, 0);
        return options;
    }, [departments]);

    const roleOptions = useMemo(() => {
        const options = [{ label: 'Tất cả vai trò', value: 0, searchKey: 'tat ca vai tro' }];
        roles.forEach(r => {
            options.push({
                label: r.Name,
                value: r.Id,
                searchKey: superCleanString(r.Name)
            });
        });
        return options;
    }, [roles]);

    const filteredData = useMemo(() => {
        let allowedDeptIds = [];

        const isFilteringDept = filters.departmentId && filters.departmentId !== 0 && filters.departmentId !== '';
        const isFilteringRole = filters.roleId && filters.roleId !== 0 && filters.roleId !== '';

        if (isFilteringDept) {
            const selectedDeptId = parseInt(filters.departmentId);
            allowedDeptIds.push(selectedDeptId);

            const findChildren = (parentId) => {
                const children = departments.filter(d => d.ParentId === parentId);
                children.forEach(child => {
                    allowedDeptIds.push(child.Id);
                    findChildren(child.Id);
                });
            };
            findChildren(selectedDeptId);
        }

        return data.filter(item => {
            if (isFilteringDept) {
                if (!allowedDeptIds.includes(item.DepartmentId)) {
                    return false;
                }
            }
            if (isFilteringRole) {
                if (item.RoleId !== parseInt(filters.roleId)) {
                    return false;
                }
            }
            if (filters.userId) {
                if (item.Id !== filters.userId) {
                    return false;
                }
            }
            return true;
        });
    }, [data, filters, departments]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!canManage) {
            toast.current?.show({ severity: 'error', summary: 'Từ chối', detail: 'Bạn không có quyền thực hiện chức năng này!' });
            return;
        }

        const method = editId ? 'PUT' : 'POST';

        const payload = {
            ...formData,
            DegreeId: formData.DegreeId ? parseInt(formData.DegreeId) : null,
            AcademicTitleId: formData.AcademicTitleId ? parseInt(formData.AcademicTitleId) : null,
            AcademicRank: formData.AcademicRank ? formData.AcademicRank : null,
            DepartmentId: formData.DepartmentId ? parseInt(formData.DepartmentId) : null,
            RoleId: formData.RoleId ? parseInt(formData.RoleId) : null,
            ManagedDepartmentId: (formData.ManagedDepartmentId !== '' && formData.ManagedDepartmentId !== null) ? parseInt(formData.ManagedDepartmentId) : null,
            IsDelegatedAdmin: !!formData.IsDelegatedAdmin
        };
        if (editId) payload.Id = editId;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user`, {
                method,
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401 || response.status === 403) return handleLogout();

            if (response.ok) {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: editId ? 'Cập nhật thành công!' : 'Thêm mới thành công!' });

                if (editId === currentUser.Id) {
                    const updatedLocalUser = {
                        ...currentUser,
                        DepartmentId: payload.DepartmentId
                    };
                    localStorage.setItem('user', JSON.stringify(updatedLocalUser));
                    window.dispatchEvent(new Event('storage'));
                }

                fetchData(true);
                closeModal();
            } else {
                const err = await response.json();
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: err.message || 'Lưu dữ liệu thất bại!' });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
        }
    };

    const fetchUserDetail = async (id) => {
        setIsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user?id=${id}&_t=${new Date().getTime()}`, {
                headers: getAuthHeaders()
            });
            if (response.ok) {
                return await response.json();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể lấy thông tin chi tiết!' });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Mất kết nối máy chủ!' });
        } finally {
            setIsLoading(false);
        }
        return null;
    };

    const handleEdit = async (item) => {
        if (!canManage) return;

        const fullItem = await fetchUserDetail(item.Id);
        if (!fullItem) return;

        setEditId(fullItem.Id);

        const cleanItem = { ...fullItem };

        if (cleanItem.LastLogin) {
            cleanItem.LastLogin = null;
        }

        setFormData({
            ...cleanItem,
            Password: '',
            ConfirmPassword: '',
            DegreeId: cleanItem.DegreeId || '',
            AcademicTitleId: cleanItem.AcademicTitleId || '',
            AcademicRank: cleanItem.AcademicRank || '',
            DepartmentId: cleanItem.DepartmentId || '',
            RoleId: cleanItem.RoleId || '',
            ManagedDepartmentId: cleanItem.ManagedDepartmentId !== null ? cleanItem.ManagedDepartmentId : '',
            IsDelegatedAdmin: cleanItem.IsDelegatedAdmin || false
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        if (!canManage) return;
        confirmDeleteDialog({
            header: 'Xác nhận xóa',
            message: 'Bạn có chắc chắn muốn xóa người dùng này?',
            accept: async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user?id=${id}`, {
                        method: 'DELETE',
                        headers: getAuthHeaders()
                    });

                    if (response.status === 401 || response.status === 403) return handleLogout();

                    if (response.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xóa người dùng!' });
                        fetchData(true);
                    } else {
                        const err = await response.json();
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: err.message || 'Xóa thất bại!' });
                    }
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
                }
            }
        });
    };

    const handleToggleRestrict = (userToToggle) => {
        confirmDeleteDialog({
            header: 'Xác nhận thay đổi quyền',
            message: `Bạn có chắc chắn muốn ${userToToggle.IsAddRestricted ? 'GỠ BỎ' : 'BẬT'} hạn chế Thêm mới đối với tài khoản ${userToToggle.Email}?`,
            accept: async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user?action=toggleRestrict`, {
                        method: 'POST',
                        headers: {
                            ...getAuthHeaders(),
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ targetUserId: userToToggle.Id, isRestricted: !userToToggle.IsAddRestricted })
                    });

                    if (response.status === 401 || response.status === 403) return handleLogout();

                    if (response.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã cập nhật quyền hạn chế!' });
                        fetchData(true);
                    } else {
                        const err = await response.json();
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: err.message });
                    }
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
                }
            }
        });
    };

    const handleMonitor = async (user) => {
        setMonitoredUser(user);
        setIsMonitorModalOpen(true);
        setIsMonitorLoading(true);
        setMonitorLogs([]);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/activitylog?userId=${user.Id}`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (response.status === 401 || response.status === 403) return handleLogout();

            if (response.ok) {
                const data = await response.json();
                setMonitorLogs(data);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể lấy dữ liệu log!' });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
        } finally {
            setIsMonitorLoading(false);
        }
    };

    const handleResetScvSync = () => {
        if (!isAdmin) return;

        confirmResetSCVDialog({
            header: 'Xác nhận Đồng bộ lại toàn bộ SCV',
            message: 'Bạn có chắc chắn muốn TẤT CẢ người dùng đồng bộ lại dữ liệu từ SCV trong lần đăng nhập tiếp theo? (Hệ thống sẽ lấy dữ liệu mới nhất)',
            accept: async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reset-scv-sync`, {
                        method: 'POST',
                        headers: getAuthHeaders()
                    });

                    const result = await response.json();
                    if (response.ok && result.status === 'success') {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã đặt lại cờ đồng bộ thành công!' });
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: result.message || 'Thất bại!' });
                    }
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
                }
            }
        });
    };

    const handleViewCV = (user) => {
        if (!canManage) return;
        const cvUrl = `/ly-lich-khoa-hoc?id=${user.Id}`;
        window.open(cvUrl, '_blank');
    };

    const closeModal = () => { setIsModalOpen(false); setFormData(initialForm); setEditId(null); };

    const handleOpenScholar = (link = '') => {
        setSelectedScholarLink(link);
        setIsScholarModalOpen(true);
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />
            <div className="page-header">
                <div className="header-title">
                    <h2>QUẢN LÝ NGƯỜI DÙNG</h2>
                </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                {canManage && (
                    <button className="btn-add-new" onClick={() => setIsModalOpen(true)} style={{ margin: 0 }}>
                        <i className="fa-solid fa-plus"></i> Thêm mới
                    </button>
                )}

                {isAdmin && (
                    <button className="btn-add-new" style={{ backgroundColor: '#f59e0b', margin: 0 }} onClick={handleResetScvSync}>
                        <i className="fa-solid fa-rotate"></i> Reset Đồng bộ SCV
                    </button>
                )}
            </div>

            <div style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '5px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                    <p className="sub-title" style={{ margin: 0 }}>DANH SÁCH NGƯỜI DÙNG</p>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', flex: 1, justifyContent: 'flex-end' }}>

                        <SearchableFocusDropdown
                            value={filters.departmentId}
                            options={departmentOptions}
                            onChange={(e) => setFilters(prev => ({ ...prev, departmentId: e.value }))}
                            placeholder="Chọn đơn vị"
                            panelUniqueClass="custom-dropdown-panel-dept-filter"
                            style={{ width: '100%', maxWidth: '300px', height: '40px', alignItems: 'center' }}
                        />

                        <SearchableFocusDropdown
                            value={filters.roleId}
                            options={roleOptions}
                            onChange={(e) => setFilters(prev => ({ ...prev, roleId: e.value }))}
                            placeholder="Chọn vai trò"
                            panelUniqueClass="custom-dropdown-panel-role-filter"
                            style={{ width: '100%', maxWidth: '200px', height: '40px', alignItems: 'center' }}
                        />

                        <div style={{ width: '100%', maxWidth: '350px' }}>
                            <AutoComplete
                                value={selectedAuthor}
                                suggestions={filteredAuthors}
                                completeMethod={searchAuthor}
                                field="label"
                                dropdown
                                placeholder="Họ tên hoặc Email giảng viên"
                                itemTemplate={userFilterTemplate}
                                onChange={(e) => {
                                    setSelectedAuthor(e.value);
                                    if (e.value && e.value.value) {
                                        setFilters(prev => ({ ...prev, userId: e.value.value }));
                                    } else {
                                        setFilters(prev => ({ ...prev, userId: null }));
                                    }
                                }}
                                style={{ width: '100%', height: '40px' }}
                                inputStyle={{ width: '100%', padding: '0 12px', border: '1px solid #ced4da', borderRadius: '4px', fontFamily: 'inherit' }}
                                panelStyle={{ width: 'auto', minWidth: '350px', maxWidth: '500px' }}
                                emptyMessage="Không tìm thấy giảng viên"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <UserListing
                data={filteredData}
                onEdit={canManage ? handleEdit : () => { }}
                onDelete={canManage ? handleDelete : () => { }}
                isLoading={isLoading}
                onOpenScholar={handleOpenScholar}
                canManage={canManage}
                onViewCV={handleViewCV}
                onToggleRestrict={handleToggleRestrict}
                currentUserEmail={currentUser.Email}
                onMonitor={handleMonitor}
            />

            <UserForm
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                isEditing={!!editId}
                departments={departments}
                roles={roles}
                degrees={degrees}
                academicTitles={academicTitles}
                currentUser={currentUser}
            />

            <AllGoogleScholarModal
                isOpen={isScholarModalOpen}
                onClose={() => setIsScholarModalOpen(false)}
                defaultLink={selectedScholarLink}
                usersList={data}
            />

            <ActivityLogModal
                isOpen={isMonitorModalOpen}
                onClose={() => setIsMonitorModalOpen(false)}
                logs={monitorLogs}
                isLoading={isMonitorLoading}
                user={monitoredUser}
            />
        </div>
    );
};

export default User;